import { useCallback } from 'react'
import personApi from '../../../api/person/PersonApi'
import { PersonDto } from '../../../api/person/models/PersonDto'
import { CsPersonSearchResultDto } from '../../../api/person/models/searchCs/SearchPersonsCsDtos'
import Modal from '../../common/modals/Modal'
import CsPersonSearch from './CsPersonSearch'

type Props = {
    onClose: () => void
    // If provided, selects by person id
    onSelectId?: (personId: string) => void | Promise<void>
    // Back-compat: if provided, selects an existing graph person
    onSelect?: (person: PersonDto) => void | Promise<void>
}

const DEBOUNCE_MS = 1000

export default function PersonSearchModal({
    onClose,
    onSelectId,
    onSelect,
}: Props) {
    const handleSelect = useCallback(
        async (person: CsPersonSearchResultDto) => {
            if (onSelectId) {
                await onSelectId(person.id)
                return
            }

            if (!onSelect) return
            if (!person.existingPersonId) {
                throw new Error('Person must be imported before selecting.')
            }

            const { person: existingPerson } = await personApi.getPersonById(
                person.existingPersonId,
            )
            await onSelect(existingPerson)
        },
        [onSelectId, onSelect],
    )

    const canSelectResult = useCallback(
        (person: CsPersonSearchResultDto) => {
            if (onSelectId) return true
            return Boolean(person.existingPersonId)
        },
        [onSelectId],
    )

    return (
        <Modal title="Find Person (CS)" onClose={onClose} size="large">
            <CsPersonSearch
                allowEmptyQuery={false}
                minQueryLength={2}
                searchDebounceMs={DEBOUNCE_MS}
                enableUrlSync={false}
                allowImport={false}
                allowNetworkImport={false}
                allowGenerateNetwork={false}
                enableWorkHistoryLinks={false}
                tableWrapperClassName="max-h-[420px] overflow-y-auto overflow-x-auto border rounded"
                searchPlaceholder="Type a name (e.g. John Smith)"
                selectButtonLabel="Select"
                selectColumnLabel=""
                emptyStateMessage="No matches"
                onSelectPerson={handleSelect}
                canSelectResult={canSelectResult}
            />
        </Modal>
    )
}
